import axios from "axios";

const API_URL = process.env.REACT_APP_MANAGEMENT_API_URL;
const API_KEY = process.env.REACT_APP_DB_PROXY_API_KEY;

const apiClient = axios.create({
  baseURL: API_URL,
  headers: { "x-api-key": API_KEY },
});

const OID_HEX = /^[a-fA-F0-9]{24}$/;

/** BSON-style ObjectId in the browser: Object.keys() is empty → would become {} on the wire. */
function tryObjectIdWire(obj) {
  if (obj == null || typeof obj !== "object" || Array.isArray(obj)) return null;
  if (typeof obj.toHexString === "function") {
    try {
      const hex = obj.toHexString();
      if (typeof hex === "string" && OID_HEX.test(hex)) return { $oid: hex };
    } catch (_) {}
  }
  if (obj._bsontype === "ObjectId" && typeof obj.toString === "function") {
    const s = String(obj).trim();
    if (OID_HEX.test(s)) return { $oid: s };
  }
  return null;
}

function normalizeInsertedId(id) {
  if (id == null) return id;
  if (typeof id === "string" && OID_HEX.test(id)) return id;
  if (
    typeof id === "object" &&
    typeof id.$oid === "string" &&
    OID_HEX.test(id.$oid)
  ) {
    return id.$oid;
  }
  return id;
}

/**
 * JSON-safe payloads for db-proxy. Date / ObjectId-like objects are not enumerable → must not use Object.keys alone.
 */
function serializeForJson(obj, parentKey) {
  if (obj === null || obj === undefined) return obj;
  if (obj instanceof Date) return obj.toISOString();
  if (obj instanceof RegExp) {
    if (parentKey === "$regex") {
      return obj.source;
    }
    return { $regex: obj.source, $options: obj.flags };
  }
  if (Array.isArray(obj)) return obj.map((v) => serializeForJson(v));
  if (typeof obj === "object") {
    const oidWire = tryObjectIdWire(obj);
    if (oidWire) return oidWire;
    const result = {};
    for (const key of Object.keys(obj)) {
      result[key] = serializeForJson(obj[key], key);
    }
    if (result.$regex instanceof RegExp) {
      result.$options = result.$regex.flags || result.$options;
      result.$regex = result.$regex.source;
    }
    return result;
  }
  return obj;
}

function createCollectionProxy(collectionName) {
  return {
    async findOne(filter) {
      const res = await apiClient.post("/db/findOne", {
        collection: collectionName,
        filter: serializeForJson(filter),
      });
      return res.data.document;
    },

    async find(filter = {}, options = {}) {
      const res = await apiClient.post("/db/find", {
        collection: collectionName,
        filter: serializeForJson(filter),
        sort:
          options.sort != null ? serializeForJson(options.sort) : options.sort,
        limit: options.limit,
      });
      return res.data.documents;
    },

    async aggregate(pipeline) {
      const res = await apiClient.post("/db/aggregate", {
        collection: collectionName,
        pipeline: serializeForJson(pipeline),
      });
      return res.data.documents;
    },

    async insertOne(document) {
      const res = await apiClient.post("/db/insertOne", {
        collection: collectionName,
        document: serializeForJson(document),
      });
      return { insertedId: normalizeInsertedId(res.data.insertedId) };
    },

    async findOneAndUpdate(filter, update) {
      const res = await apiClient.post("/db/findOneAndUpdate", {
        collection: collectionName,
        filter: serializeForJson(filter),
        update: serializeForJson(update),
      });
      return res.data.document;
    },
  };
}

function createDbProxy(dbName) {
  return {
    collection(name) {
      return createCollectionProxy(name);
    },
  };
}

export function createMongoProxy() {
  return {
    currentUser: {
      mongoClient() {
        return {
          db(name) {
            return createDbProxy(name);
          },
        };
      },
    },
    mongoClient() {
      return {
        db(name) {
          return createDbProxy(name);
        },
      };
    },
  };
}
